<template>
    <edit-resource
        :form="form"
        :title="title"
        @close="$emit('close')"
        @submit="saveLocation"
    >
        <div class="w-768">
            <label class="flex mb-8" for="search-location">
                {{ $t('locations.search_select') }}:
            </label>

            <div class="w-full h-360 relative rounded overflow-hidden mb-32">
                <div class="absolute w-2/3 z-10 pt-16 pl-16">
                    <input
                        id="search-location"
                        ref="search"
                        v-model="query"
                        :placeholder="$t('settings.locations.search_placeholder')"
                        autocomplete="off"
                        class="border border-primary-20 shadow-md rounded h-40 px-16 outline-none w-full mb-8"
                        type="search"
                        @input="handleInput"
                    >

                    <div class="bg-white shadow-md rounded overflow-hidden w-full border-primary-20">
                        <div
                            v-for="(suggestion, index) in suggestions"
                            :key="suggestion.id"
                            :class="[ index === (suggestions.length - 1) ? '' : 'border-b-2 border-primary-20' ]"
                            class="w-full px-16 py-8 hover:bg-primary-5 trans-ease cursor-pointer"
                            @click="selectSuggestion(suggestion)"
                        >
                            {{ suggestion.street + ', ' + suggestion.postal_code + ' ' + suggestion.city + ', ' + suggestion.region + ' ' + suggestion.country.text }}
                        </div>
                    </div>
                </div>

                <div id="mapbox-container" class="h-full w-full"></div>
            </div>

            <div class="flex">
                <div class="w-1/2 mr-32">
                    <translatable #default="{ languages, selectedLanguage }" :errors="warningTabs" class="w-full mb-16">
                        <div v-for="lang in languages" :key="lang">
                            <simple-input v-show="lang === selectedLanguage" v-model="form.alias[lang]">
                                {{ $t('locations.alias') }}
                                <span v-if="languages.length > 1">({{ lang }})</span>
                                <span class="text-body-sm">{{ $t('general.optional') }}</span>
                            </simple-input>
                        </div>
                    </translatable>
                    <simple-input v-model="form.group">
                        {{ $t('locations.group') }}
                        <span class="text-body-sm">{{ $t('general.optional') }}</span>
                    </simple-input>
                </div>

                <div class="w-1/2">
                    <div class="pt-16 mb-32">
                        <checkbox v-model="form.is_hq">{{ $t('locations.this_is_hq') }}</checkbox>
                    </div>
                    <simple-input v-model="form.phone" class="mb-16">
                        {{ $t('locations.phone') }}
                        <span class="text-body-sm">{{ $t('general.optional') }}</span>
                    </simple-input>
                    <simple-input v-model="form.email">
                        {{ $t('locations.email') }}
                        <span class="text-body-sm">{{ $t('general.optional') }}</span>
                    </simple-input>
                </div>
            </div>
        </div>
    </edit-resource>
</template>

<script>
import EditResource from './EditResource.vue';
import Input from '../forms/Input.vue';
import _debounce from 'lodash/debounce';
import geocoder from '@mapbox/mapbox-sdk/services/geocoding';
import mbxClient from '../../etc/MapboxClient';
import Form from '../../etc/forms/Form';
import Checkbox from '../forms/Checkbox.vue';
import Translatable from '../forms/Translatable.vue';
import * as locations from '../../services/LocationService';
import Location from '../../models/Location';
import {normalizeJson} from '../../../shared/helpers';
import {mapState} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../../state/store';

function getFields() {
    return [
        {name: 'street', value: '', rules: ['required']},
        {name: 'postal_code', value: '', rules: ['required']},
        {name: 'city', value: '', rules: ['required']},
        {name: 'region', value: ''},
        {name: 'group', value: ''},
        {name: 'phone', value: ''},
        {name: 'email', value: ''},
        {name: 'lat', value: '', rules: ['required']},
        {name: 'lng', value: '', rules: ['required']},
        {name: 'country_code', value: '', rules: ['required']},
        {name: 'alias', value: {}, translatable: true},
        {name: 'is_hq', value: false},
    ];
}

export default {
    components: {
        'edit-resource': EditResource,
        'simple-input': Input,
        'checkbox': Checkbox,
        'translatable': Translatable,
    },
    props: {
        location: Object,
    },
    data: function () {
        return {
            geocoder: null,
            query: '',
            map: null,
            suggestions: [],
            selection: null,
            form: this.generateForm(),
            marker: null,
            popup: null,
            savingLocation: false,
        };
    },
    computed: {
        ...mapState(useMainStore, ['currentCompany']),
        title() {
            return this.$t(this.location ? 'general.edit_subject' : 'general.add_subject', {subject: this.$t('settings.locations.location')});
        },
        warningTabs() {
            let languages = this.currentCompany.jobsite.active_languages;
            let errorLang = [];

            for (let index in languages) {
                if (!this.form.data.alias[languages[index]]) {
                    errorLang.push(languages[index]);
                }
            }
            return errorLang;
        },
    },
    methods: {
        generateForm() {
            let fields = getFields();

            if (this.location) {
                fields.forEach((field, index) => {
                    fields[index].value = this.location[field.name] ? JSON.parse(JSON.stringify(this.location[field.name])) : null;
                });
            }

            return new Form(fields);
        },
        handleInput: _debounce(function () {
            this.getGeocodeResults();
        }, 500),
        getGeocodeResults() {

            if (this.query.length < 4) {
                this.suggestions = [];
                return;
            }

            this.geocoder.forwardGeocode({
                query: this.query,
                types: ['address'],
                // languages should be
                language: [],
                limit: 5,
            })
                .send()
                .then(response => {
                    this.suggestions = response.body.features.map(feature => {
                        let data = {
                            street: feature.text,
                        };

                        if (feature.address) {
                            data.street += (' ' + feature.address);
                        }

                        data.postal_code = feature.context.find(entry => {
                            return entry.id.includes('postcode');
                        })?.text || '';

                        const locality = feature.context.find(entry => {
                            return entry.id.includes('locality');
                        });
                        const place = feature.context.find(entry => {
                            return entry.id.includes('place');
                        });

                        data.city = locality ? locality.text : place.text;

                        data.country = feature.context.find(entry => {
                            return entry.id.includes('country');
                        });

                        data.region = feature.context.find(entry => {
                            return entry.id.includes('region');
                        })?.text || '';

                        data.center = feature.center;

                        return data;
                    });
                });
        },
        selectSuggestion(suggestion) {
            this.query = '';
            this.suggestions = [];
            this.$refs.search?.focus();

            this.form.street = suggestion.street;
            this.form.postal_code = suggestion.postal_code;
            this.form.city = suggestion.city;
            this.form.country_code = suggestion.country.short_code.toUpperCase();
            this.form.region = suggestion.region;
            this.form.lat = suggestion.center[1];
            this.form.lng = suggestion.center[0];

            this.addSelectionMarker();
            this.flyToSelection();
        },
        addSelectionMarker() {

            if (this.marker) {
                this.marker.remove();
            }

            if (this.popup) {
                this.popup.remove();
            }

            this.marker = new mapboxgl.Marker()
                .setLngLat([this.form.lng, this.form.lat])
                .addTo(this.map);

            this.popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                anchor: 'top',
                offset: 15,
            })
                .setLngLat([this.form.lng, this.form.lat])
                .setHTML(`
                <div class="rounded bg-white shadow-md p-16 flex flex-col text-body-sm">
                    <span class="font-semibold">${this.form.street}</span>
                    <span>${this.form.postal_code} ${this.form.city}</span>
                </div>
            `)
                .addTo(this.map);
        },
        flyToSelection() {

            this.map.flyTo({
                speed: 2,
                center: [this.form.lng, this.form.lat],
                zoom: 14,
            });
        },
        async saveLocation() {
            if (this.location) {
                await this.updateLocation();
            } else {
                let location = await this.createLocation();

                this.$emit('created', location.id);
            }

            this.$emit('close');
        },
        updateLocation() {
            return this.form.submit(data => locations.update(this.location.id, {
                data: {attributes: data},
            })).then(result => {
                if (result.attributes.is_hq) {
                    //update all locations is_hq to false when current is set
                    useRepo(Location).where('is_hq', true).update({ is_hq: false });
                }

                return useRepo(Location).save(normalizeJson(result));
            });
        },
        createLocation() {
            return this.form.submit(data => locations.store({
                data: {
                    attributes: {
                        ...data,
                        order: useRepo(Location).orderBy('order', 'desc').first().order + 1,
                    },
                },
            })).then(result => {
                return useRepo(Location).save(normalizeJson(result));
            });
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.map = new mapboxgl.Map({
                container: 'mapbox-container',
                attributionControl: false,
                style: 'mapbox://styles/mapbox/streets-v11',
                zoom: 0,
                scrollZoom: false,
                doubleClickZoom: false,
                touchZoomRotate: false,
                keyboard: false,
                dragRotate: false,
                boxZoom: false,
                renderWorldCopies: true,
            });

            this.geocoder = geocoder(mbxClient);

            if (this.location) {
                this.addSelectionMarker();
                this.map.jumpTo({
                    center: [this.form.lng, this.form.lat],
                    zoom: 14,
                });
            }
        });
    },
};
</script>
